import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";
import { LeafletMap } from "@/components/LeafletMap";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtTime } from "@/lib/format";
import {
  ArrowLeft, MapPin, Phone, Navigation, Truck, Loader2, CheckCircle2, AlertTriangle,
  Camera, Layers, Package, Boxes, FileText, Trophy, X,
} from "lucide-react";

const CONDITIONS = ["Delivered in full", "Partial delivery", "Damaged goods", "Goods refused", "Other"];
const PHOTO_CATS = ["Goods delivered", "Damaged goods", "Delivery location", "Other"];

export default function DeliveryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");

  const { data: o, isLoading, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: async () => (await api.get(`/orders/${id}`)).data,
  });

  // delivery-confirmation form state
  const [goods, setGoods] = useState(null);
  const [receiver, setReceiver] = useState({ receiver_name: "", receiver_company: "", receiver_phone: "" });
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    const dd = o?.delivery;
    if (dd?.receiver_name) {
      setReceiver((r) => (r.receiver_name ? r : { receiver_name: dd.receiver_name, receiver_company: dd.receiver_company || "", receiver_phone: dd.receiver_phone || "" }));
    }
  }, [o]);

  const refresh = async () => { await refetch(); qc.invalidateQueries({ queryKey: ["driver-orders"] }); };

  if (isLoading || !o) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF6500]" /></div>;

  const d = o.delivery || {};
  const cs = o.customer_snapshot || {};
  const g = goods || {
    pallets_delivered: o.pallets, cartons_delivered: o.cartons,
    items_delivered: o.total_items, condition: "Delivered in full",
    condition_notes: "", location_reason: "",
  };
  const setG = (patch) => setGoods({ ...g, ...patch });

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); await refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };

  const start = () => act("start", async () => {
    await api.post(`/orders/${id}/start`);
    toast.success("Delivery started — In Transit");
  });

  const arrive = () => act("arrive", async () => {
    const coords = await new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("GPS not available on this device"));
      navigator.geolocation.getCurrentPosition(
        (p) => resolve(p),
        () => reject(new Error("Location permission denied — enable GPS to mark arrival")),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
    await api.post(`/orders/${id}/arrive`, {
      latitude: coords.coords.latitude, longitude: coords.coords.longitude,
      accuracy: Math.round(coords.coords.accuracy),
    });
    toast.success("✓ Location captured");
  });

  const saveGoods = () => api.post(`/orders/${id}/goods`, g);

  const sign = (dataUrl) => act("sign", async () => {
    if (!receiver.receiver_name.trim()) { toast.error("Enter receiver name first"); throw new Error("name"); }
    await saveGoods();
    await api.post(`/orders/${id}/sign`, { ...receiver, signature: dataUrl });
    setSigned(true);
    toast.success("Signature captured");
  });

  const complete = () => act("complete", async () => {
    if (!window.confirm("Confirm that this delivery has been completed?")) throw new Error("cancel");
    await saveGoods();
    await api.post(`/orders/${id}/complete`);
    toast.success(`POD generated for #${o.order_number}`);
  });

  const addPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => act("photo", async () => {
      await api.post(`/orders/${id}/photos`, { data: reader.result, category: "Goods delivered" });
      toast.success("Photo attached");
    });
    reader.readAsDataURL(file);
  };

  const hasGps = cs.latitude != null;
  const gmapsUrl = hasGps
    ? `https://www.google.com/maps/dir/?api=1&destination=${cs.latitude},${cs.longitude}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cs.delivery_address)}`;
  const wazeUrl = hasGps
    ? `https://waze.com/ul?ll=${cs.latitude},${cs.longitude}&navigate=yes`
    : `https://waze.com/ul?q=${encodeURIComponent(cs.delivery_address)}&navigate=yes`;

  const isDone = ["Completed", "Partial Delivery", "Failed Delivery"].includes(o.status);
  const showGPS = d.arrival_lat != null;

  return (
    <div className="animate-slide-up">
      <button onClick={() => navigate("/driver")} className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500" data-testid="back-to-jobs">
        <ArrowLeft size={18} /> Run sheet
      </button>

      {/* Header card */}
      <div className="rounded-2xl bg-[#0B192C] p-5 text-white">
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm font-bold text-[#FF6500]">#{o.order_number}</span>
          <StatusBadge status={o.status} />
        </div>
        <h1 className="mt-1 font-head text-xl font-extrabold">{cs.name}</h1>
        <div className="mt-2 flex items-start gap-2 text-sm text-slate-300">
          <MapPin size={16} className="mt-0.5 shrink-0" /> {cs.delivery_address}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-white/10 py-2"><div className="text-xl font-black">{o.pallets}</div><div className="text-[10px] uppercase text-slate-400">Pallets</div></div>
          <div className="rounded-xl bg-white/10 py-2"><div className="text-xl font-black">{o.cartons}</div><div className="text-[10px] uppercase text-slate-400">Cartons</div></div>
          <div className="rounded-xl bg-white/10 py-2"><div className="text-xl font-black">{o.total_items}</div><div className="text-[10px] uppercase text-slate-400">Items</div></div>
        </div>
        {(cs.contact_person || cs.phone) && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-300">
            <Phone size={15} /> {cs.contact_person} {cs.phone && `· ${cs.phone}`}
          </div>
        )}
      </div>

      {/* nav + contact buttons */}
      <div className={`mt-3 grid gap-2 ${cs.phone ? "grid-cols-3" : "grid-cols-2"}`}>
        <a href={gmapsUrl} target="_blank" rel="noreferrer" data-testid="open-google-maps-btn" className="flex flex-col items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white py-2.5 text-xs font-semibold text-[#0B192C] tap-shrink">
          <Navigation size={18} className="text-[#1a73e8]" /> Google Maps
        </a>
        <a href={wazeUrl} target="_blank" rel="noreferrer" data-testid="open-waze-btn" className="flex flex-col items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white py-2.5 text-xs font-semibold text-[#0B192C] tap-shrink">
          <Navigation size={18} className="text-[#33ccff]" /> Waze
        </a>
        {cs.phone && <a href={`tel:${cs.phone}`} data-testid="contact-customer-btn" className="flex flex-col items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white py-2.5 text-xs font-semibold text-[#0B192C] tap-shrink">
          <Phone size={18} /> Contact
        </a>}
      </div>

      {/* ===== STEP: START ===== */}
      {["Assigned", "Accepted"].includes(o.status) && (
        <button
          onClick={start} disabled={busy}
          data-testid="driver-start-delivery-btn"
          className="mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-lg font-bold text-white shadow-md tap-shrink hover:bg-blue-700 active:scale-95 disabled:opacity-60"
        >
          {busy === "start" ? <Loader2 className="animate-spin" /> : <><Truck size={22} /> START DELIVERY</>}
        </button>
      )}

      {/* ===== STEP: ARRIVED ===== */}
      {o.status === "In Transit" && (
        <button
          onClick={arrive} disabled={busy}
          data-testid="driver-arrived-btn"
          className="mt-4 flex min-h-[60px] w-full items-center justify-center gap-2 rounded-xl bg-purple-600 text-xl font-black text-white shadow-md tap-shrink hover:bg-purple-700 active:scale-95 disabled:opacity-60"
        >
          {busy === "arrive" ? <Loader2 className="animate-spin" /> : <><MapPin size={24} /> ARRIVED</>}
        </button>
      )}

      {/* GPS capture result */}
      {showGPS && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="gps-capture-card">
          <div className="flex items-center gap-2 font-bold text-emerald-700"><CheckCircle2 size={18} /> Location Captured</div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
            <div><div className="text-xs text-slate-400">Latitude</div><div className="font-mono font-semibold">{d.arrival_lat?.toFixed(6)}</div></div>
            <div><div className="text-xs text-slate-400">Longitude</div><div className="font-mono font-semibold">{d.arrival_lng?.toFixed(6)}</div></div>
            <div><div className="text-xs text-slate-400">Accuracy</div><div className="font-mono font-semibold">{d.gps_accuracy ?? "-"} m</div></div>
          </div>
          <div className="mt-3">
            <LeafletMap height={160} points={[{ lat: d.arrival_lat, lng: d.arrival_lng, status: o.status, label: "Recorded location" }]} />
          </div>
          {d.location_confirmed ? (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700" data-testid="location-confirmed">
              <CheckCircle2 size={16} /> Delivery Location Confirmed
            </div>
          ) : (
            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="location-warning">
              <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={16} /> Location Warning</div>
              <p className="mt-1">Driver appears to be {d.distance_from_customer}m from the registered delivery location.</p>
            </div>
          )}
        </div>
      )}

      {/* ===== STEP: CONFIRMATION FORM (Arrived) ===== */}
      {o.status === "Arrived" && (
        <div className="mt-4 space-y-4" data-testid="delivery-confirmation-form">
          {/* goods */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="flex items-center gap-2 font-head font-bold text-[#0B192C]"><Boxes size={18} /> Goods Delivered</h3>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[["Pallets", "pallets_delivered", Layers], ["Cartons", "cartons_delivered", Package], ["Items", "items_delivered", Boxes]].map(([lab, key, Icon]) => (
                <div key={key}>
                  <label className="text-xs font-semibold text-slate-500">{lab}</label>
                  <input
                    type="number" min="0" value={g[key]}
                    data-testid={`goods-${key}`}
                    onChange={(e) => setG({ [key]: parseInt(e.target.value || "0") })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg font-bold outline-none focus:border-[#0B192C]"
                  />
                </div>
              ))}
            </div>
            <label className="mt-3 block text-xs font-semibold text-slate-500">Delivery condition</label>
            <div className="mt-1 grid grid-cols-1 gap-1.5">
              {CONDITIONS.map((c) => (
                <button
                  key={c} onClick={() => setG({ condition: c })}
                  data-testid={`condition-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium ${
                    g.condition === c ? "border-[#0B192C] bg-slate-50 text-[#0B192C]" : "border-slate-200 text-slate-600"
                  }`}
                >
                  <span className={`h-3.5 w-3.5 rounded-full border-2 ${g.condition === c ? "border-[#FF6500] bg-[#FF6500]" : "border-slate-300"}`} />
                  {c}
                </button>
              ))}
            </div>
            {g.condition !== "Delivered in full" && (
              <textarea
                placeholder="Reason / notes" value={g.condition_notes}
                data-testid="condition-notes"
                onChange={(e) => setG({ condition_notes: e.target.value })}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#0B192C]"
                rows={2}
              />
            )}
            {!d.location_confirmed && (
              <textarea
                placeholder="Reason for location difference (optional)" value={g.location_reason}
                data-testid="location-reason"
                onChange={(e) => setG({ location_reason: e.target.value })}
                className="mt-2 w-full rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm outline-none"
                rows={2}
              />
            )}
          </div>

          {/* photos */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="flex items-center gap-2 font-head font-bold text-[#0B192C]"><Camera size={18} /> Photos</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {(d.photos || []).map((p) => (
                <img key={p.id} src={p.data} alt={p.category} className="h-16 w-16 rounded-lg object-cover" />
              ))}
              <label data-testid="add-photo-btn" className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 tap-shrink">
                {busy === "photo" ? <Loader2 className="animate-spin" size={18} /> : <Camera size={20} />}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhoto} />
              </label>
            </div>
          </div>

          {/* receiver */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="font-head font-bold text-[#0B192C]">Received By</h3>
            <input
              placeholder="Receiver full name *" value={receiver.receiver_name}
              data-testid="receiver-name-input"
              onChange={(e) => setReceiver({ ...receiver, receiver_name: e.target.value })}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3 text-sm outline-none focus:border-[#0B192C]"
            />
            <input
              placeholder="Company / position (optional)" value={receiver.receiver_company}
              data-testid="receiver-company-input"
              onChange={(e) => setReceiver({ ...receiver, receiver_company: e.target.value })}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]"
            />
            <input
              placeholder="Phone (optional)" value={receiver.receiver_phone}
              data-testid="receiver-phone-input"
              onChange={(e) => setReceiver({ ...receiver, receiver_phone: e.target.value })}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]"
            />
          </div>

          {/* signature */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="font-head font-bold text-[#0B192C]">Customer Signature</h3>
            <p className="mb-2 text-sm text-slate-500">Please sign below to confirm receipt of the goods.</p>
            {signed || d.signature ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="signature-captured">
                <img src={d.signature} alt="signature" className="mx-auto h-24" />
                <div className="mt-1 flex items-center justify-center gap-1.5 text-sm font-bold text-emerald-700">
                  <CheckCircle2 size={16} /> SIGNED {fmtTime(d.signed_at)} · {d.receiver_name}
                </div>
              </div>
            ) : (
              <SignaturePad onAccept={sign} />
            )}
          </div>

          {/* complete */}
          <button
            onClick={complete} disabled={busy || !(signed || d.signature)}
            data-testid="driver-complete-delivery-btn"
            className="flex min-h-[64px] w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 text-xl font-black tracking-wide text-white shadow-lg tap-shrink hover:bg-emerald-700 active:scale-95 disabled:opacity-40"
          >
            {busy === "complete" ? <Loader2 className="animate-spin" /> : <><CheckCircle2 size={24} /> COMPLETE DELIVERY</>}
          </button>
          {!(signed || d.signature) && <p className="-mt-2 text-center text-xs text-slate-400">Capture receiver name & signature to complete</p>}
        </div>
      )}

      {/* ===== DONE ===== */}
      {isDone && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-white p-5 text-center" data-testid="delivery-complete-card">
          <Trophy className="mx-auto text-[#FF6500]" size={40} />
          <h2 className="mt-2 font-head text-xl font-extrabold text-[#0B192C]">Delivery {o.status}</h2>
          <p className="text-sm text-slate-500">POD {o.pod_number}</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-left text-sm">
            <Info label="Arrival" value={fmtTime(d.arrived_at)} />
            <Info label="Completed" value={fmtTime(d.completed_at)} />
            <Info label="Time on site" value={d.time_on_site_minutes != null ? `${d.time_on_site_minutes} min` : "-"} />
            <Info label="Received by" value={d.receiver_name} />
          </div>
          <button
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/pod/pdf`, "_blank")}
            data-testid="driver-download-pod-btn"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B192C] py-3 font-bold text-white tap-shrink"
          >
            <FileText size={18} /> View / Download POD
          </button>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="font-semibold text-[#0B192C]">{value || "-"}</div>
    </div>
  );
}
